/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

export interface IFormInputProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'id' | 'type' | 'value' | 'className' | 'aria-required'> {
    id: string;
    type: string;
    value?: string;
    isRequired: boolean;
    onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
}

export const FormInput: React.FC<IFormInputProps> = ({ id, type, value, isRequired, onChange, ...rest }) => (
    <input id={id} type={type} value={value} aria-required={isRequired} onChange={onChange} {...rest} />
);

export interface IFormTextAreaProps {
    id: string;
    value?: string;
    className: string;
    isRequired: boolean;
    onChange?(event: React.ChangeEvent<HTMLTextAreaElement>): void;
}

export const FormTextArea: React.FC<IFormTextAreaProps> = ({ id, value, className, isRequired, onChange }) => (
    <textarea id={id} name={id} value={value} className={`${className}-input-area`} aria-required={isRequired} onChange={onChange} />
);

export interface IFormDropdownOption {
    key: string;
    value: string;
}

export interface IFormDropdownProps {
    id: string;
    value?: string;
    className: string;
    isRequired: boolean;
    options: IFormDropdownOption[];
    placeholder?: string;
    onChange?(event: React.ChangeEvent<HTMLSelectElement>): void;
}

export const FormDropdown: React.FC<IFormDropdownProps> = ({ id, value, className, isRequired, options, placeholder, onChange }) => (
    <select id={id} name={id} value={value} className={`${className}-select`} aria-required={isRequired} onChange={onChange}>
        {placeholder && <option value=''>{placeholder}</option>}
        {options.map((option, index) => (
            <option key={index} value={option.key}>
                {option.value}
            </option>
        ))}
    </select>
);

export interface IFormRadioOptionProps {
    id: string;
    name: string;
    value: string;
    label: string;
    className: string;
    checked?: boolean;
    onChange?(event: React.ChangeEvent<HTMLInputElement>): void;
}

export const FormRadioOption: React.FC<IFormRadioOptionProps> = ({ id, name, value, label, className, checked, onChange }) => (
    <div className={`${className}-radio-option`}>
        <input type='radio' id={id} name={name} value={value} checked={checked} onChange={onChange} />
        <label htmlFor={id}>{label}</label>
    </div>
);
